// Datenübernahme aus der alten App.
//
// Die alte Oberfläche hielt Kunden, Lieferanten und Artikel im localStorage
// desselben Browsers. Diese Seite liest sie dort aus und schickt sie ans
// Backend. Der Import ist wiederholbar: bereits übernommene Einträge werden
// erkannt und übersprungen.
import { useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten } from '../../../gemeinsam/lib/daten';
import { Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Stand = {
  kunden: number; lieferanten: number; produkte: number; rechnungen: number;
  importiert: { kunden: number; lieferanten: number; produkte: number };
};

type Bericht = {
  kunden: { neu: number; uebersprungen: number };
  lieferanten: { neu: number; uebersprungen: number };
  produkte: { neu: number; uebersprungen: number };
  fehler: string[];
};

/** Liest einen localStorage-Wert und gibt ihn als Array zurück, egal wie er verpackt war. */
function lese(schluessel: string): unknown[] {
  try {
    const roh = localStorage.getItem(schluessel);
    if (!roh) return [];
    const wert = JSON.parse(roh);
    if (Array.isArray(wert)) return wert;
    // Manche Schlüssel enthalten ein Objekt mit der Liste darin.
    if (wert && typeof wert === 'object') {
      for (const k of ['items', 'data', 'list', 'customers', 'suppliers', 'products']) {
        if (Array.isArray((wert as Record<string, unknown>)[k])) return (wert as Record<string, unknown[]>)[k];
      }
      return Object.values(wert).filter(v => v && typeof v === 'object');
    }
    return [];
  } catch {
    return [];
  }
}

/** Sucht alle Schlüssel, die zu einem Bereich passen — die alte App hat sie teils pro Mandant abgelegt. */
function sammle(praefixe: string[]): unknown[] {
  const treffer: unknown[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (praefixe.some(p => k === p || k.startsWith(p))) treffer.push(...lese(k));
    }
  } catch { /* kein Zugriff, dann eben nichts */ }
  return treffer;
}

export default function Uebernahme() {
  const { daten: stand, laedt, neuLaden } = useDaten<Stand>('/migration/stand');
  const [gefunden, setGefunden] = useState<{ kunden: unknown[]; lieferanten: unknown[]; produkte: unknown[] } | null>(null);
  const [bericht, setBericht] = useState<Bericht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  function suchen() {
    setFehler(null);
    setBericht(null);
    setGefunden({
      kunden: sammle(['lv_customers', 'lp_customers', 'customers']),
      lieferanten: sammle(['lp_suppliers_', 'lp_suppliers', 'suppliers']),
      produkte: sammle(['lp_products', 'lv_products', 'products', 'lp_artikel', 'inventory']),
    });
  }

  async function uebernehmen() {
    if (!gefunden) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const ergebnis = await api<Bericht>('/migration/import', { methode: 'POST', daten: gefunden });
      setBericht(ergebnis);
      await neuLaden();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Übernahme fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  }

  if (laedt && !stand) return <div className="flex justify-center py-16"><Spinner /></div>;

  const gesamtGefunden = gefunden
    ? gefunden.kunden.length + gefunden.lieferanten.length + gefunden.produkte.length
    : 0;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Daten übernehmen</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Aus der alten App in dieses Konto
        </p>
      </div>

      <Hinweis art="info" titel="Warum das nötig ist">
        Die alte Anwendung hat Kunden, Lieferanten und Artikel nur im Browser gespeichert. Dadurch
        waren die Daten an dieses Gerät gebunden, für Kollegen unsichtbar und beim Leeren des
        Browser-Speichers verloren. Nach der Übernahme liegen sie auf dem Server.
      </Hinweis>

      {stand && (
        <Karte>
          <KartenKopf titel="Bereits auf dem Server" />
          <div className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-4">
            {[
              ['Kunden', stand.kunden], ['Lieferanten', stand.lieferanten],
              ['Artikel', stand.produkte], ['Rechnungen', stand.rechnungen],
            ].map(([titel, wert]) => (
              <div key={titel as string}>
                <p className="text-xs text-slate-500 dark:text-slate-400">{titel}</p>
                <p className="text-xl font-bold text-slate-900 dark:text-slate-100">{wert as number}</p>
              </div>
            ))}
          </div>
        </Karte>
      )}

      <Karte>
        <KartenKopf
          titel="Schritt 1 — Im Browser suchen"
          hinweis="Diese Seite muss im selben Browser geöffnet sein, in dem Sie die alte App benutzt haben."
        />
        <div className="space-y-4 p-5">
          <Knopf variante="primaer" onClick={suchen}>Nach alten Daten suchen</Knopf>

          {gefunden && (
            gesamtGefunden === 0 ? (
              <Hinweis art="warnung" titel="Nichts gefunden">
                In diesem Browser liegen keine Daten der alten App. Falls Sie sie auf einem anderen
                Gerät oder in einem anderen Browser genutzt haben, öffnen Sie diese Seite dort.
              </Hinweis>
            ) : (
              <>
                <ul className="space-y-1 text-sm text-slate-700 dark:text-slate-300">
                  <li>{gefunden.kunden.length} Kunden</li>
                  <li>{gefunden.lieferanten.length} Lieferanten</li>
                  <li>{gefunden.produkte.length} Artikel</li>
                </ul>
                <Knopf variante="primaer" laedt={laeuft} onClick={uebernehmen}>
                  Schritt 2 — {gesamtGefunden} Einträge übernehmen
                </Knopf>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Die Daten im Browser bleiben unangetastet. Ein zweiter Durchlauf legt keine Dubletten an.
                </p>
              </>
            )
          )}

          {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

          {bericht && (
            <Hinweis art="erfolg" titel="Übernahme abgeschlossen">
              <ul className="mt-1 space-y-0.5">
                <li>{bericht.kunden.neu} Kunden übernommen{bericht.kunden.uebersprungen > 0 && `, ${bericht.kunden.uebersprungen} bereits vorhanden`}</li>
                <li>{bericht.lieferanten.neu} Lieferanten übernommen{bericht.lieferanten.uebersprungen > 0 && `, ${bericht.lieferanten.uebersprungen} bereits vorhanden`}</li>
                <li>{bericht.produkte.neu} Artikel übernommen{bericht.produkte.uebersprungen > 0 && `, ${bericht.produkte.uebersprungen} bereits vorhanden`}</li>
              </ul>
              {bericht.fehler.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer">{bericht.fehler.length} Einträge übersprungen</summary>
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {bericht.fehler.slice(0, 20).map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </details>
              )}
            </Hinweis>
          )}
        </div>
      </Karte>
    </div>
  );
}
